import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router'
import { api } from '../api/client'
import type { CrashDetail } from '../api/crashes'
import { StackTable } from '../components/admin/crashes/StackTable'
import { useCrashAction } from '../components/admin/crashes/useCrashAction'
import { formatDateTime } from '../lib/format'

/** One crash or hang report: what it is, its stack and the whole raw diagnostic. */
export function CrashReportScreen() {
	const { id = '' } = useParams()
	const navigate = useNavigate()
	const detail = useQuery({
		queryKey: ['admin-crash', id],
		queryFn: () => api.get<CrashDetail>(`/api/v1/admin/crashes/${id}`),
	})
	const resymbolicate = useCrashAction(() => api.post(`/api/v1/admin/crashes/${id}/resymbolicate`))
	const resolve = useCrashAction((reopen: boolean) => api.post('/api/v1/admin/crashes/resolve', { ids: [id], reopen }))
	const remove = useCrashAction(() => api.delete(`/api/v1/admin/crashes/${id}`))
	const r = detail.data?.report
	const facts: [string, string | null][] = r
		? [
				['Source', r.source],
				['App', `${r.bundleId} ${r.appVersion ?? ''} (${r.appBuild ?? '?'})`],
				['OS', r.osVersion],
				['Model', r.model],
				['Distribution', r.isSimulator ? 'simulator' : r.distribution],
				['Occurred', formatDateTime(r.occurredAt)],
				['Received', formatDateTime(r.receivedAt)],
				['Exception', [r.exceptionType, r.exceptionCode, r.signal].filter(Boolean).join(' / ') || null],
				['Termination reason', r.terminationReason],
				['Exception reason', r.exceptionReason],
				['Install', r.installId],
				['Account', r.accountId],
				['Device', r.deviceId],
			]
		: []
	const download = () => {
		const url = URL.createObjectURL(
			new Blob([JSON.stringify(detail.data!.payload, null, 2)], { type: 'application/json' }),
		)
		const a = Object.assign(document.createElement('a'), { href: url, download: `crash-${id}.json` })
		a.click()
		URL.revokeObjectURL(url)
	}

	return (
		<div className="stack">
			<div className="page-header">
				<div>
					<Link to={r ? `/admin/crashes/group/${r.signature}?days=365` : '/admin?tab=crashes'} className="small">
						← Stack group
					</Link>
					<h1 style={{ margin: '0.2rem 0 0', fontFamily: 'var(--mono)' }}>
						{r?.title ?? `${r?.kind ?? 'Crash'} report`}
					</h1>
					{r && (
						<div className="muted small">
							{r.symbolicationState}
							{r.resolvedAt && ` · resolved ${formatDateTime(r.resolvedAt)}`}
							{r.duplicateOf && (
								<>
									{' · '}MetricKit’s copy of <Link to={`/admin/crashes/${r.duplicateOf}`}>the extension’s report</Link>,
									which is the one that counts
								</>
							)}
							{detail.data!.copies.length > 0 && ' · also delivered by MetricKit'}
						</div>
					)}
				</div>
			</div>
			{detail.isError && <p className="error">{detail.error.message}</p>}
			{r && (
				<>
					<div className="row">
						<button type="button" disabled={resymbolicate.isPending} onClick={() => resymbolicate.mutate(undefined)}>
							Re-symbolicate
						</button>
						{!r.duplicateOf && (
							<button type="button" onClick={() => resolve.mutate(r.resolvedAt !== null)}>
								{r.resolvedAt ? 'Reopen' : 'Mark resolved'}
							</button>
						)}
						<button
							type="button"
							className="danger"
							onClick={() =>
								window.confirm('Delete this report? There is no undo.') &&
								remove.mutate(undefined, { onSuccess: () => navigate('/admin?tab=crashes') })
							}
						>
							Delete
						</button>
					</div>
					<section className="card stack">
						<h2 style={{ margin: 0 }}>{detail.data!.stack.heading}</h2>
						<StackTable stack={detail.data!.stack} />
					</section>
					<section className="card">
						<h2>Details</h2>
						<dl className="facts">
							{facts.map(([label, value]) => (
								<div key={label}>
									<dt>{label}</dt>
									<dd className="small">{value ?? '—'}</dd>
								</div>
							))}
						</dl>
					</section>
					<section className="card stack">
						<div className="row" style={{ justifyContent: 'space-between' }}>
							<h2 style={{ margin: 0 }}>Raw diagnostic (all threads)</h2>
							<button type="button" onClick={download}>
								Download JSON
							</button>
						</div>
						<pre className="small" style={{ maxHeight: '28rem', overflow: 'auto', margin: 0 }}>
							{JSON.stringify(detail.data!.payload, null, 2)}
						</pre>
					</section>
				</>
			)}
		</div>
	)
}
